
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Download, Trash2, Eye, AlertTriangle } from 'lucide-react';

export const DataManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleExportData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t('common:gdpr.dataManagement.accountDeletion.notAuthenticated'));

      // Récupérer toutes les données utilisateur
      const [profileData, puttingResults, levelResults, individualScores] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id),
        supabase.from('putting_test_results').select('*').eq('user_id', user.id),
        supabase.from('level_tests_results').select('*').eq('user_id', user.id),
        supabase.from('individual_scores').select('*').eq('user_id', user.id)
      ]);

      const exportData = {
        export_date: new Date().toISOString(),
        user_info: {
          id: user.id,
          email: user.email,
          created_at: user.created_at
        },
        profile: profileData.data?.[0] || null,
        putting_results: puttingResults.data || [],
        level_results: levelResults.data || [],
        individual_scores: individualScores.data || []
      };

      // Créer et télécharger le fichier JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `golf-progress-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: t('common:gdpr.dataManagement.dataAccess.exportSuccess'),
        description: t('common:gdpr.dataManagement.dataAccess.exportSuccessDescription'),
      });
    } catch (error: any) {
      toast({
        title: t('common:common.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t('common:gdpr.dataManagement.accountDeletion.notAuthenticated'));

      // Supprimer toutes les données utilisateur
      await Promise.all([
        supabase.from('putting_test_results').delete().eq('user_id', user.id),
        supabase.from('level_tests_results').delete().eq('user_id', user.id),
        supabase.from('individual_scores').delete().eq('user_id', user.id),
        supabase.from('user_favorite_tests').delete().eq('user_id', user.id),
        supabase.from('golf_shot_history').delete().eq('user_id', user.id)
      ]);

      // Supprimer le compte utilisateur de manière sécurisée via Edge Function
      const { error } = await supabase.functions.invoke('secure-user-deletion', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        }
      });
      if (error) throw error;

      toast({
        title: t('common:gdpr.dataManagement.accountDeletion.deleteSuccess'),
        description: t('common:gdpr.dataManagement.accountDeletion.deleteSuccessDescription'),
      });

      // Redirection vers la page d'accueil
      window.location.href = '/';
    } catch (error: any) {
      toast({
        title: t('common:common.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {t('common:gdpr.dataManagement.dataAccess.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            {t('common:gdpr.dataManagement.dataAccess.description')}
          </p>
          <Button onClick={handleExportData} disabled={loading}>
            <Download className="h-4 w-4 mr-2" />
            {t('common:gdpr.dataManagement.dataAccess.downloadButton')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            {t('common:gdpr.dataManagement.accountDeletion.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{t('common:gdpr.dataManagement.accountDeletion.warning')}</strong> {t('common:gdpr.dataManagement.accountDeletion.warningDescription')}
            </AlertDescription>
          </Alert>

          {!showDeleteConfirm ? (
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteConfirm(true)}
            >
              {t('common:gdpr.dataManagement.accountDeletion.deleteButton')}
            </Button>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('common:gdpr.dataManagement.accountDeletion.confirmQuestion')}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteAllData}
                  disabled={loading}
                >
                  {t('common:gdpr.dataManagement.accountDeletion.confirmDelete')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                >
                  {t('common:gdpr.dataManagement.accountDeletion.cancel')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
